<template>
  <div>
    <confirm-dialog ref="confirmDeleteDialog"
                    confirm-button-color="deep-orange"
                    v-bind:cancel-button-label="$t('cancel')"
                    v-bind:confirm-button-label="$t('delete')"
                    v-bind:message="$t('areYouSureYouWantToDeleteThisTraineeIrrevocably')"
                    v-bind:title="$t('deleteTrainee')"
                    v-model="confirmDeleteDialog" />

    <v-snackbar color="deep-orange"
                right
                top
                v-model="validationSnackbar">
      {{ $t('thereAreItemsThatRequireYourAttention') }}
    </v-snackbar>

    <top-header v-bind:title="formattedName | defaultValue($t('trainee'))">
      <header-button icon="mdi-chevron-left"
                     slot="left-button"
                     v-bind:label="$t('back')"
                     v-bind:to="{ name: 'learning-trainees' }" />

      <header-button icon="mdi-check"
                     right
                     slot="right-button"
                     v-bind:label="$t('save')"
                     v-on:click="updateTrainee" />

      <v-menu slot="menu">
        <template v-slot:activator="{ on, attrs }">
          <v-btn dark
                 icon
                 v-bind="attrs"
                 v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-bind:to="{ name: 'learning-my-exams', params: { invitationKey }}">
            <v-list-item-title>{{ $t('traineeView') }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-on:click="deleteTrainee">
            <v-list-item-title class="deep-orange--text">{{ $t('delete') }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </top-header>

    <v-progress-linear indeterminate
                       v-bind:active="loading" />

    <v-container v-if="trainee != null">
      <v-row>
        <v-col cols="12"
               lg="6">
          <trainee-details-card ref="traineeDetailsCard"
                                show-invitation-key
                                v-bind:trainee="trainee" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  import ConfirmDialog from '@/components/common/ConfirmDialog'
  import HeaderButton from '@/components/common/HeaderButton.vue'
  import TopHeader from '@/components/common/TopHeader.vue'
  import TraineeDetailsCard from '@/components/learning/TraineeDetailsCard.vue'

  export default {
    async beforeRouteUpdate(to, from, next) {
      const id = parseInt(to.params.id)

      await this.get(id)

      next()
    },
    components: {
      ConfirmDialog,
      HeaderButton,
      TopHeader,
      TraineeDetailsCard
    },
    computed: {
      formattedName() {
        if (this.trainee == null) {
          return null
        }

        return `${this.trainee.givenName} ${this.trainee.familyName}`
      },
      id() {
        return this.trainee?.id
      },
      invitationKey() {
        return this.trainee?.invitationKey
      },
      ...mapGetters({
        getTrainee: 'learning/trainee',
        traineeFormattedName: 'learning/traineeFormattedName'
      })
    },
    async created() {
      const id = parseInt(this.$route.params.id)

      await this.get(id)
    },
    data: () => ({
      confirmDeleteDialog: false,
      loading: false,
      trainee: null,
      validationSnackbar: false
    }),
    methods: {
      async deleteTrainee() {
        await this.$refs.confirmDeleteDialog.open(async r => {
          if (!r) {
            return
          }

          this.loading = true

          await this.doDeleteTrainee({ id: this.id })

          this.loading = false

          this.$router.push({ name: 'learning-trainees' })
        })
      },
      async get(id) {
        this.loading = true

        await this.loadDepartments()
        await this.loadLearningTenants()

        await this.loadTrainee({ id })

        this.loading = false

        this.trainee = Object.assign({}, this.getTrainee(id))
      },
      async updateTrainee() {
        if (!this.$refs.traineeDetailsCard.validate()) {
          this.validationSnackbar = true

          return
        }

        this.loading = true

        await this.doUpdateTrainee({ trainee: this.trainee })

        this.loading = false

        this.$router.push({ name: 'learning-trainees' })
      },
      ...mapActions({
        doDeleteTrainee: 'learning/deleteTrainee',
        doUpdateTrainee: 'learning/updateTrainee',
        loadDepartments: 'common/loadDepartments',
        loadLearningTenants: 'learning/loadLearningTenants',
        loadTrainee: 'learning/loadTrainee'
      })
    },
    name: 'Trainee'
  }
</script>

<i18n>
  {
    "de": {
      "areYouSureYouWantToDeleteThisTraineeIrrevocably": "Sind Sie sicher, dass Sie diesen Trainee unwiderruflich löschen möchten?",
      "back": "Zurück",
      "cancel": "Abbrechen",
      "delete": "Löschen",
      "deleteTrainee": "Trainee löschen",
      "save": "Speichern",
      "thereAreItemsThatRequireYourAttention": "Es gibt Felder, die Ihre Aufmerksamkeit erfordern.",
      "trainee": "Trainee",
      "traineeView": "Traineeansicht"
    },
    "en": {
      "areYouSureYouWantToDeleteThisTraineeIrrevocably": "Are you sure you want to delete this trainee irrevocably?",
      "back": "Back",
      "cancel": "Cancel",
      "delete": "Delete",
      "deleteTrainee": "Delete trainee",
      "save": "Save",
      "thereAreItemsThatRequireYourAttention": "There are items that require your attention.",
      "trainee": "Trainee",
      "traineeView": "Trainee view"
    }
  }
</i18n>